/**
 * AccessDeniedAlert — Displays a dismissible notification when a user
 * is redirected to their correct dashboard after attempting to access
 * a route they don't have permission for.
 *
 * Reads `accessDenied` from the router location state (set by ProtectedRoute).
 * Automatically clears the state after displaying so the alert doesn't persist
 * on subsequent navigations.
 */

import { useEffect, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

export default function AccessDeniedAlert() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const state = location.state as { accessDenied?: boolean; attemptedPath?: string } | null;

  useEffect(() => {
    if (state?.accessDenied) {
      setOpen(true);
      // Clear the state so the alert doesn't reappear on refresh or back navigation
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [state, navigate, location.pathname]);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={5000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert
        onClose={handleClose}
        severity="warning"
        variant="filled"
        sx={{ width: '100%' }}
      >
        Access denied. You have been redirected to your dashboard.
      </Alert>
    </Snackbar>
  );
}
